'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import Breadcrumb from '@/components/Breadcrumb';

interface DnsRecord {
  id: string;
  hostname: string;
  tipo: string;
  valore: string;
  zona: string;
  ttl: number;
  alias_di: string | null;
  device_id: string | null;
  note: string;
  created_at: string;
}

const tipoColors: Record<string, string> = {
  A: 'bg-green-500/20 text-green-400',
  AAAA: 'bg-sky-500/20 text-sky-400',
  CNAME: 'bg-violet-500/20 text-violet-400',
  MX: 'bg-amber-500/20 text-amber-400',
  TXT: 'bg-gray-500/20 text-gray-400',
  PTR: 'bg-pink-500/20 text-pink-400',
  NS: 'bg-orange-500/20 text-orange-400',
};

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

const emptyForm = { hostname: '', tipo: 'A', valore: '', zona: 'local', ttl: 3600, note: '' };

export default function DnsManagementContent() {
  const [records, setRecords] = useState<DnsRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [zonaFilter, setZonaFilter] = useState('all');
  const [tipoFilter, setTipoFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DnsRecord | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [zones, setZones] = useState<string[]>([]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('dns_records').select('*').order('zona').order('hostname');
      if (error) throw error;
      setRecords(data || []);
      const uniqueZones = [...new Set((data || []).map((r: DnsRecord) => r.zona))];
      setZones(uniqueZones);
    } catch (err: any) {
      toast.error('Errore caricamento DNS: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const filtered = records.filter(r => {
    const matchZona = zonaFilter === 'all' || r.zona === zonaFilter;
    const matchTipo = tipoFilter === 'all' || r.tipo === tipoFilter;
    const matchSearch = !search || r.hostname.toLowerCase().includes(search.toLowerCase()) || r.valore.toLowerCase().includes(search.toLowerCase());
    return matchZona && matchTipo && matchSearch;
  });

  const openCreate = () => { setEditingRecord(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (r: DnsRecord) => {
    setEditingRecord(r);
    setForm({ hostname: r.hostname, tipo: r.tipo, valore: r.valore, zona: r.zona, ttl: r.ttl, note: r.note });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.hostname || !form.valore) { toast.error('Hostname e valore obbligatori'); return; }
    setSaving(true);
    try {
      if (editingRecord) {
        const { error } = await supabase.from('dns_records').update(form).eq('id', editingRecord.id);
        if (error) throw error;
        toast.success('Record DNS aggiornato');
      } else {
        const { error } = await supabase.from('dns_records').insert(form);
        if (error) throw error;
        toast.success('Record DNS creato');
      }
      setShowModal(false);
      fetchRecords();
    } catch (err: any) {
      toast.error('Errore salvataggio: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, hostname: string) => {
    if (!confirm(`Eliminare il record DNS "${hostname}"?`)) return;
    try {
      const { error } = await supabase.from('dns_records').delete().eq('id', id);
      if (error) throw error;
      toast.success('Record eliminato');
      fetchRecords();
    } catch (err: any) {
      toast.error('Errore eliminazione: ' + err.message);
    }
  };

  return (
    <div className="flex flex-col h-full bg-background">
      <Toaster position="bottom-right" theme="dark" richColors />
      <Breadcrumb items={[{ label: 'Gestione DNS' }]} />
      <div className="px-6 py-5 border-b border-border bg-surface flex-shrink-0">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white">DNS Interni</h1>
            <p className="text-sm text-secondary-color mt-0.5">Gestione hostname, alias e zone DNS</p>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-400 transition-all"
          >
            <Icon name="PlusIcon" size={16} />
            Nuovo Record
          </button>
        </div>
      </div>

      <div className="px-6 py-3 border-b border-border bg-surface flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-color" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Cerca hostname o valore..."
            className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:border-sky-500/50"
          />
        </div>
        <div className="relative">
          <select value={zonaFilter} onChange={e => setZonaFilter(e.target.value)} className="bg-surface-elevated border border-border text-secondary-color text-sm font-semibold rounded-lg px-3 py-2 focus:outline-none appearance-none pr-7 cursor-pointer">
            <option value="all">Tutte le zone</option>
            {zones.map(z => <option key={z} value={z}>{z}</option>)}
          </select>
          <Icon name="ChevronDownIcon" size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-color pointer-events-none" />
        </div>
        <div className="relative">
          <select value={tipoFilter} onChange={e => setTipoFilter(e.target.value)} className="bg-surface-elevated border border-border text-secondary-color text-sm font-semibold rounded-lg px-3 py-2 focus:outline-none appearance-none pr-7 cursor-pointer">
            <option value="all">Tutti i tipi</option>
            {['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'PTR', 'NS'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <Icon name="ChevronDownIcon" size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-color pointer-events-none" />
        </div>
        <span className="text-sm text-muted-color font-semibold ml-auto">{filtered.length} record</span>
      </div>

      <div className="flex-1 overflow-auto scrollbar-thin">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Icon name="GlobeAltIcon" size={40} className="text-muted-color" />
            <p className="text-secondary-color font-semibold">Nessun record DNS trovato</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-surface border-b border-border z-10">
              <tr>
                {['Hostname', 'Tipo', 'Valore', 'Zona', 'TTL', 'Note', 'Azioni'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold text-muted-color uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(r => (
                <tr key={r.id} className="hover:bg-surface-elevated/30 transition-colors">
                  <td className="px-4 py-3 font-mono text-white font-semibold">{r.hostname}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${tipoColors[r.tipo] || 'bg-gray-500/20 text-gray-400'}`}>{r.tipo}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-secondary-color text-xs">{r.valore}</td>
                  <td className="px-4 py-3 text-secondary-color">{r.zona}</td>
                  <td className="px-4 py-3 text-secondary-color">{r.ttl}s</td>
                  <td className="px-4 py-3 text-muted-color text-xs max-w-32 truncate">{r.note || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(r)} className="p-1.5 rounded-lg hover:bg-surface-elevated text-muted-color hover:text-white transition-colors">
                        <Icon name="PencilIcon" size={14} />
                      </button>
                      <button onClick={() => handleDelete(r.id, r.hostname)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-color hover:text-red-400 transition-colors">
                        <Icon name="TrashIcon" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="text-lg font-bold text-white">{editingRecord ? 'Modifica Record DNS' : 'Nuovo Record DNS'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-surface-elevated transition-colors">
                <Icon name="XMarkIcon" size={18} className="text-muted-color" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Hostname *</label>
                  <input type="text" value={form.hostname} onChange={e => setForm({ ...form, hostname: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" placeholder="es. server.local" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Tipo</label>
                  <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50">
                    {['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'PTR', 'NS'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Valore *</label>
                <input type="text" value={form.valore} onChange={e => setForm({ ...form, valore: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" placeholder="es. 10.10.1.1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Zona</label>
                  <input type="text" value={form.zona} onChange={e => setForm({ ...form, zona: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" placeholder="es. local" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">TTL (secondi)</label>
                  <input type="number" value={form.ttl} onChange={e => setForm({ ...form, ttl: parseInt(e.target.value) || 3600 })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Note</label>
                <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} rows={2} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50 resize-none" />
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-border">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 border border-border text-secondary-color rounded-lg text-sm font-semibold hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-400 transition-all disabled:opacity-50">
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
